import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { Breeder, Listing, PetAge, MarketplaceProduct, ProductType, User } from 'shared/@types';
import { KeyValueErrors } from 'shared/library/use-api';

export interface AveragePrice {
  breed_id: number | null;
  price: number;
  currency: string;
  pet_age: PetAge;
}

export interface ListingWizardSupport {
  email: string;
  firstName: string;
  acceptDeposit: boolean | null;
  addLitterInsurance: boolean | null;
  hasLitterInsuranceDetails: boolean;
  hasBreeder: boolean;
  createBreeder: boolean | null;
  breederName: string;
  addedProducts: ProductType[];
}

const defaultSupport: ListingWizardSupport = {
  email: '',
  firstName: '',
  acceptDeposit: null,
  addLitterInsurance: null,
  hasLitterInsuranceDetails: false,
  hasBreeder: false,
  createBreeder: null,
  breederName: '',
  addedProducts: [],
};

const useListingWizardStore = create(
  combine(
    {
      data: null as Listing | null,
      support: defaultSupport,
      errors: {} as KeyValueErrors,
      averagePrices: [] as AveragePrice[],
      products: [] as MarketplaceProduct[],
    },
    (set) => ({
      setup: (
        user: User | null,
        breeder: Breeder | null,
        data: Listing,
        averagePrices: AveragePrice[],
        products: MarketplaceProduct[],
      ) => {
        set({
          data,
          support: {
            email: user?.email ?? '',
            firstName: user?.first_name ?? '',
            acceptDeposit: (() => {
              if (data.deposit === null) return null;
              return data.deposit > 0;
            })(),
            addLitterInsurance: (() => {
              if (data.litter_insurance) return true;
              if (data.litter_insurance_company) return false;
              return null;
            })(),
            hasLitterInsuranceDetails: !!data.litter_insurance,
            hasBreeder: !!breeder,
            createBreeder: data.payments.length > 0 ? false : null,
            breederName: '',
            addedProducts: data.payments[0]?.payment_items.map((item) => item.product_type) ?? [],
          },
          averagePrices,
          products,
        });
      },
      reset: () =>
        set({
          data: null,
          support: defaultSupport,
          errors: {},
        }),
      setData: (newData: Partial<Listing>) => {
        set((state) => ({ data: { ...state.data!, ...newData } }));
      },
      setSupport: (newData: Partial<ListingWizardSupport>) => {
        set((state) => ({ support: { ...state.support, ...newData } }));
      },
    }),
  ),
);

export default useListingWizardStore;
